<impersonation-banner>
    <!-- Spacer to push content down -->
    <div class="banner-spacer {state.collapsed ? 'collapsed' : ''}" if="{shouldShowBanner()}"></div>
    
    <!-- Fixed banner that overlays -->
    <div class="impersonation-banner-wrapper {state.collapsed ? 'collapsed' : ''} {getWrapperClass()}" if="{shouldShowBanner()}">
        <div class="impersonation-banner" onmouseenter="{expandBanner}" onmouseleave="{maybeCollapseBanner}">
            <div class="container mx-auto flex items-center justify-between">
                <!-- Full content (hidden when collapsed) -->
                <div class="flex items-center gap-3 full-content">
                    <div class="flex items-center gap-2">
                        <i class="icon-metalark-user"></i>
                        <span class="font-semibold">{locale?.t('admin_impersonation_label')}:</span>
                        <!--
                          "Customer / Workspace" pair. Customer name is the main
                          anchor when an admin has juggled several concurrent
                          sessions; the slice name disambiguates which workspace
                          within that customer they're acting inside.
                        -->
                        <span if="{state.targetCustomerName}">{state.targetCustomerName}</span>
                        <span if="{state.targetCustomerName}" class="opacity-80 mx-1">/</span>
                        <span>{state.targetSliceName || state.targetSliceId}</span>
                    </div>
                    <div class="text-sm opacity-75" if="{state.reason}">
                        <i class="icon-metalark-info"></i>
                        {state.reason}
                    </div>
                </div>

                <!-- Collapsed content (shown when collapsed) -->
                <div class="collapsed-content">
                    <span class="collapsed-slice-name">
                        <span if="{state.targetCustomerName}">{state.targetCustomerName} / </span>{state.targetSliceName || state.targetSliceId}
                    </span>
                </div>
                
                <!-- Full buttons (hidden when collapsed) -->
                <div class="flex items-center gap-3 full-content">
                    <div class="flex items-center gap-2 text-sm">
                        <i class="icon-metalark-clock"></i>
                        <span>{formatTime(state.remainingSeconds)}</span>
                    </div>
                    <button
                        class="btn btn-sm btn-ghost hover:bg-white/20"
                        onclick="{returnToAdmin}">
                        <i class="icon-metalark-arrow-back me-1"></i>
                        {locale?.t('admin_impersonation_returntoadmin')}
                    </button>
                    <button
                        class="btn btn-sm btn-ghost hover:bg-white/20 hover:bg-red-500/20"
                        onclick="{endImpersonation}">
                        <i class="icon-metalark-close me-1"></i>
                        {locale?.t('admin_impersonation_endsession_button')}
                    </button>
                </div>
                
                <!-- Collapsed timer (shown when collapsed) -->
                <div class="collapsed-content">
                    <span class="collapsed-timer">{formatTime(state.remainingSeconds)}</span>
                </div>
            </div>
        </div>
    </div>

    <impersonation-end-modal ref="endModal"></impersonation-end-modal>

    <style>
        impersonation-banner {
            display: block;
        }

        /* Spacer takes up space in document flow */
        .banner-spacer {
            height: 50px;
            transition: height 0.3s ease;
        }

        .banner-spacer.collapsed {
            height: 28px;
        }

        /* Fixed banner overlays at top */
        .impersonation-banner-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            z-index: 1000;
            transition: all 0.3s ease;
        }

        .impersonation-banner {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            color: #451a03;
            padding: 10px 16px;
            box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
            border-radius: 0 0 12px 12px;
            transition: all 0.3s ease;
            cursor: pointer;
        }

        /* Full content - shown when expanded */
        .full-content {
            opacity: 1;
            transition: opacity 0.2s ease;
        }

        .impersonation-banner-wrapper.collapsed .full-content {
            opacity: 0;
            height: 0;
            overflow: hidden;
            display: none;
        }

        /* Collapsed content - shown when collapsed */
        .collapsed-content {
            opacity: 0;
            height: 0;
            overflow: hidden;
            display: none;
            transition: opacity 0.2s ease;
        }

        .impersonation-banner-wrapper.collapsed .collapsed-content {
            opacity: 1;
            height: auto;
            display: block;
        }

        .collapsed-slice-name {
            font-size: 11px;
            font-weight: 600;
            font-family: monospace;
        }

        .collapsed-timer {
            font-size: 11px;
            font-weight: 600;
        }

        /* Collapsed state - compact but visible */
        .impersonation-banner-wrapper.collapsed .impersonation-banner {
            padding: 4px 16px;
        }
        
        impersonation-banner i {
            font-size: 14px;
        }

        .impersonation-banner .btn {
            color: #451a03;
            border-color: rgba(120, 53, 15, 0.2);
        }

        .impersonation-banner .btn:hover {
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(120, 53, 15, 0.3);
        }

        /* Spacer handles the space, fixed banner overlays */
    </style>

    <script>
        import { getImpersonationService, getSessionActivityService } from '../../services/service-loader';
        import { sessionCache } from '../../services/sessionCache.js'
        import impersonationEndModal from '../admin/modals/impersonation-end-modal.tag'

        export default {
            includeTools: false,
            includeStore: true,

            get components() {

                return { impersonationEndModal };

            },
            state: {
                isImpersonating: false,
                targetSliceId: null,
                targetSliceName: null,
                targetCustomerName: null,
                remainingSeconds: 0,
                reason: null,
                expiresAt: null,
                collapsed: false,
                scrolled: false
            },

            async onMounted() {
                // Record activity when user interacts with banner
                // Defer listener setup to next tick to ensure $el is available
                await new Promise(resolve => setTimeout(resolve, 0))
                
                if (this.$el) {
                    this.$el.addEventListener('click', async () => {
                        const sessionActivityService = await getSessionActivityService()
                        sessionActivityService.forceRecordActivity()
                    })
                    this.$el.addEventListener('mouseenter', async () => {
                        const sessionActivityService = await getSessionActivityService()
                        sessionActivityService.forceRecordActivity()
                    })
                } else if (this.root) {
                    // Fallback: use root element if $el unavailable
                    this.root.addEventListener('click', async () => {
                        const sessionActivityService = await getSessionActivityService()
                        sessionActivityService.forceRecordActivity()
                    })
                    this.root.addEventListener('mouseenter', async () => {
                        const sessionActivityService = await getSessionActivityService()
                        sessionActivityService.forceRecordActivity()
                    })
                }
                
                // Function to update from store state
                const updateFromStore = () => {
                    const storeState = this.store.getState()
                    // Show the red impersonation banner only for IMPERSONATION-
                    // kind sessions. Direct-role WORKSPACE_ACCESS sessions
                    // populate the same Redux slice (shared backend table)
                    // but are first-class roles, not privilege-elevation,
                    // and deliberately have no banner — the Customer /
                    // Workspace label in the navbar header is the sole
                    // indicator they're inside a workspace session.
                    //
                    // Absent kind on older session rows is treated as
                    // IMPERSONATION for back-compat (matches middleware and
                    // QueryContextTypes.isImpersonating logic).
                    const isImpersonationKind = storeState.impersonation
                        && storeState.impersonation.expiresAt
                        && (storeState.impersonation.kind ?? 'IMPERSONATION') === 'IMPERSONATION'
                    if (isImpersonationKind) {
                        // If the backend response didn't include slice/customer
                        // names (older deploy, pre-redeploy mock, etc.), fall
                        // back to sessionCache — the names were written there
                        // when this browser started the session.
                        const cached = sessionCache.getSession(storeState.impersonation.targetSliceId);
                        const targetSliceName = storeState.impersonation.targetSliceName
                            || cached?.targetSliceName
                            || storeState.impersonation.targetSliceId;
                        const targetCustomerName = storeState.impersonation.targetCustomerName
                            || cached?.targetCustomerName;
                        this.update({
                            isImpersonating: true,
                            targetSliceId: storeState.impersonation.targetSliceId,
                            targetSliceName,
                            targetCustomerName,
                            reason: storeState.impersonation.reason,
                            expiresAt: storeState.impersonation.expiresAt
                        })
                        this.startTimer()
                    } else {
                        this.update({
                            isImpersonating: false
                        })
                        this.stopTimer()
                    }
                }

                // Subscribe to store for impersonation state changes
                this.unsubscribe = this.store.subscribe(updateFromStore)

                // IMPORTANT: Check initial state immediately
                // This ensures the banner appears on page load if session is cached
                updateFromStore()

                // Add scroll listener to collapse/expand banner
                window.addEventListener('scroll', this.handleScroll.bind(this))
            },

            onUnmounted() {
                if (this.unsubscribe) {
                    this.unsubscribe()
                }
                this.stopTimer()
                window.removeEventListener('scroll', this.handleScroll)
            },

            expandBanner() {
                this.update({ collapsed: false })
            },

            maybeCollapseBanner() {
                // Only collapse if user has scrolled down
                if (this.state.scrolled) {
                    this.update({ collapsed: true })
                }
            },

            handleScroll() {
                const scrolled = window.scrollY > 50
                if (scrolled !== this.state.scrolled) {
                    this.update({
                        scrolled: scrolled,
                        collapsed: scrolled
                    })
                }
            },

            startTimer() {
                if (this.timer) return // Already running
                
                this.updateRemainingTime()
                this.timer = setInterval(() => {
                    this.updateRemainingTime()
                }, 1000)
            },

            stopTimer() {
                if (this.timer) {
                    clearInterval(this.timer)
                    this.timer = null
                }
            },

            async updateRemainingTime() {
                if (!this.state.expiresAt) return
                
                const now = new Date()
                const expires = new Date(this.state.expiresAt)
                const remaining = Math.max(0, Math.floor((expires - now) / 1000))
                
                this.update({ remainingSeconds: remaining })
                
                // Auto-end if expired
                if (remaining === 0) {
                    this.stopTimer()
                    this.store.dispatch({ type: 'clearImpersonation' })
                    // Clear the content-access JWT cookie alongside the session
                    document.cookie = 'launch_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
                    alert(this.locale.t('alert_session_expired'))
                    window.location.reload()
                }
            },

            shouldShowBanner() {
                return this.state.isImpersonating
            },

            getWrapperClass() {
                return this.state.collapsed ? 'collapsed' : ''
            },

            formatTime(seconds) {
                if (seconds < 0) return '00:00:00'
                
                const hours = Math.floor(seconds / 3600)
                const minutes = Math.floor((seconds % 3600) / 60)
                const secs = seconds % 60
                
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
            },

            returnToAdmin() {
                // Clear current session from cache
                // The session stays active on the server and can be resumed
                sessionCache.clearCurrent()
                this.store.dispatch({ type: 'clearImpersonation' })
                this.store.dispatch({ type: 'setSliceId', payload: null })

                // Remove the impersonation token - fall back to cookie auth
                localStorage.removeItem('authToken')
                localStorage.removeItem('workspace_sliceId')
                localStorage.removeItem('workspace_sliceName')

                // Clear the content-access JWT so it doesn't shadow
                // auth_token on subsequent player/content requests
                document.cookie = 'launch_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'

                // Navigate to admin page
                window.location.hash = '#admin'
                window.scrollTo(0, 0)
                window.location.reload()
            },

            async resolveEndModal() {
                if (this.$refs?.endModal && typeof this.$refs.endModal.show === 'function') {
                    return this.$refs.endModal
                }
                for (let attempt = 0; attempt < 3; attempt++) {
                    const modals = this.$$('impersonation-end-modal')
                    const modal = modals && modals.length > 0 && modals[0]._tag ? modals[0]._tag : null
                    if (modal) return modal
                    if (attempt < 2) {
                        await new Promise(resolve => setTimeout(resolve, 50))
                    }
                }
                return null
            },

            async endImpersonation() {
                const label = this.state.targetCustomerName
                    ? `${this.state.targetCustomerName} / ${this.state.targetSliceName || this.state.targetSliceId}`
                    : (this.state.targetSliceName || this.state.targetSliceId)
                const message = this.locale.t('admin_impersonation_confirm_end', { workspace: label })

                const modal = await this.resolveEndModal()
                if (!modal) {
                    // Fallback to browser confirm if modal not available —
                    // in the banner case the admin is *always* inside the
                    // session, so the native confirm captures End intent;
                    // Pause is only reachable via the modal.
                    console.error('[Impersonation Banner] End modal unavailable, using browser confirm')
                    if (!confirm(message)) return
                    await this.endSessionOnServer()
                    return
                }

                // Banner is only ever shown *while* impersonating, so Pause is
                // always a meaningful choice here.
                const result = await modal.show({
                    title: this.locale.t('admin_impersonation_endsession'),
                    message,
                    showPause: true,
                })

                if (result === 'cancel') return
                if (result === 'pause') {
                    // Pause is functionally identical to the existing
                    // "Return to Admin" button — same cache/state/cookie
                    // clean-up, same reload. Go through returnToAdmin() so
                    // the two paths stay in lockstep.
                    this.returnToAdmin()
                    return
                }
                // result === 'end'
                await this.endSessionOnServer()
            },

            async endSessionOnServer() {
                try {
                    const currentSliceId = sessionCache.getCurrentSliceId()
                    const impersonationService = await getImpersonationService()
                    await impersonationService.end()

                    // Remove from cache
                    if (currentSliceId) {
                        sessionCache.removeSession(currentSliceId)
                    }

                    this.store.dispatch({ type: 'clearImpersonation' })

                    // Clear the content-access JWT cookie alongside the session
                    document.cookie = 'launch_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
                    localStorage.removeItem('authToken')

                    window.location.hash = '#home'
                    window.location.reload()
                } catch (error) {
                    console.error('Failed to end impersonation:', error)
                    alert(this.locale.t('alert_session_end_failed'))
                }
            }
        }
    </script>
</impersonation-banner>